/**
 * The two bars that sit above everything.
 *
 * **The platform's announcement** is read from the site, so it reaches the sign-in page
 * as well as the editor — a maintenance notice is no use only to people already in.
 *
 * **A support session** is the more important one: while somebody from Praxadon is
 * looking at this student's editor, the student is told so in words, told why, told
 * until when, and told that nothing can be changed. It is not dismissible, because it
 * is not an advertisement.
 *
 * And the third thing here is the question that comes before any of that: a request to
 * look, which the person themselves answers. Nothing is opened until they do.
 */

import { Button, cn, toast } from '@praxadon/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { describeError } from '../api/client.ts';
import { support } from '../api/endpoints.ts';
import { useSession } from './session.tsx';

export function Announcement() {
  const { site } = useSession();
  const announcement = site.announcement;
  if (!announcement) return null;
  return (
    <div
      role="status"
      className={cn(
        'px-4 py-2 text-center text-sm',
        announcement.tone === 'maintenance'
          ? 'bg-warning/15 text-warning'
          : announcement.tone === 'warning'
            ? 'bg-danger/15 text-danger'
            : 'bg-accent/10 text-fg',
      )}
    >
      {announcement.message}
    </div>
  );
}

/** Shown to the student for the whole time support can see their screen. */
export function SupportBar() {
  const { session } = useSession();
  const live = session?.authenticated ? session.support : undefined;
  if (!live) return null;
  return (
    <div role="alert" className="bg-accent px-4 py-2 text-center text-sm text-accent-fg">
      Somebody from Praxadon support is looking at your editor until{' '}
      {new Date(live.until).toLocaleTimeString()}. They cannot change anything.{' '}
      <span className="opacity-80">({live.reason})</span>
    </div>
  );
}

/** The question itself: may support look? Only the person can answer it. */
export function SupportRequest() {
  const client = useQueryClient();
  const { session } = useSession();
  const signedIn = session?.authenticated === true;
  const asked = useQuery({
    queryKey: ['support', 'pending'],
    queryFn: () => support.pending(),
    enabled: signedIn,
    refetchInterval: 20_000,
  });

  const answer = useMutation({
    mutationFn: (job: { id: string; agreed: boolean }) => support.answer(job.id, job.agreed),
    onSuccess: async (_result, job) => {
      toast({ title: job.agreed ? 'They can look now' : 'Refused' });
      await client.invalidateQueries({ queryKey: ['support'] });
    },
    onError: (error) => {
      toast({ title: "That didn't work", description: describeError(error), tone: 'error' });
    },
  });

  const pending = asked.data?.pending;
  if (!pending || pending.consented) return null;

  return (
    <div role="alert" className="border-b border-border bg-surface-raised px-4 py-3 text-sm">
      <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-3">
        <span className="flex-1">
          <strong className="text-fg">Praxadon support would like to look at your editor.</strong>
          <span className="block text-fg-muted">
            {pending.reason} — they could see your work until{' '}
            {new Date(pending.expiresAt).toLocaleTimeString()}, and change nothing. You can say no.
          </span>
        </span>
        <Button
          size="sm"
          disabled={answer.isPending}
          onClick={() => {
            answer.mutate({ id: pending.id, agreed: true });
          }}
        >
          Let them look
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={answer.isPending}
          onClick={() => {
            answer.mutate({ id: pending.id, agreed: false });
          }}
        >
          No
        </Button>
      </div>
    </div>
  );
}
